import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteAgitemdetail,
  fetchAllAgitemdetails,
  fetchOneAgitemdetail,
  fetchCompleteAgitemdetails,
  updateAgitemdetail,
  fetchTaxAndPricelistOfItems,
  fetchDetailsWithSize,
  checkItemDetailUsage,
  editItemDetail,
} from "services/agitemdetail";

export const useAllAgitemdetails = (filters = {}) => {
  const query = useQuery({
    queryKey: ["all-agitemdetails", filters],
    queryFn: () => fetchAllAgitemdetails(filters),
  });
  return query;
};

export const useOneAgitemdetail = (id, enabled = true) => {
  const query = useQuery({
    queryKey: ["one-agitemdetail", id],
    queryFn: () => fetchOneAgitemdetail(id),
    enabled: enabled && Boolean(id),
  });
  return query;
};

export const useCompleteAgitemdetails = (filters = {}) => {
  const query = useQuery({
    queryKey: ["complete-agitemdetails", filters],
    queryFn: () => fetchCompleteAgitemdetails(filters),
  });
  return query;
};

export const useItemsTaxAndPriceListDetails = () => {
  return useMutation({
    queryKey: ["tax-pricelist-all-agitems"],
    mutationFn: (data) => fetchTaxAndPricelistOfItems(data),
  });
};

export const useDeleteAgitemdetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    queryKey: ["delete-agitemdetail"],
    mutationFn: (id) => deleteAgitemdetail(id),
    onSuccess: (data, variables) => {
      // Invalidate agitem and agitemdetail queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["search-all-agitems"] });
      queryClient.invalidateQueries({
        queryKey: ["search-all-agitems-without-size"],
      });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["complete-agitemdetails"] });
    },
  });
};

export const useUpdateAgitemdetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    queryKey: ["update-agitemdetail"],
    onSuccess: (data, variables) => {
      // Invalidate agitem and agitemdetail queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["one-agitemdetail"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["complete-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["search-all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
    },
    mutationFn: (data) => updateAgitemdetail(data),
  });
};

export const useDetailsWithSize = (itemCodeWithoutSize, enabled = true) => {
  const query = useQuery({
    queryKey: ["details-with-size", itemCodeWithoutSize],
    queryFn: () => fetchDetailsWithSize(itemCodeWithoutSize),
    enabled: enabled && Boolean(itemCodeWithoutSize),
  });
  return query;
};

export const useCheckItemDetailUsage = (id, enabled = true) => {
  const query = useQuery({
    queryKey: ["check-item-detail-usage", id],
    queryFn: () => checkItemDetailUsage(id),
    enabled: enabled && Boolean(id),
    staleTime: 0, // Always fetch fresh data for usage check
  });
  return query;
};

export const useEditItemDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => editItemDetail(data),
    onSuccess: (data, variables) => {
      // Invalidate agitem and agitemdetail queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["all-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["complete-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["one-agitemdetail"] });
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["search-all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
    },
  });
};
